import { Fragment, use } from "react";

import { RadioField } from "@bokio/designsystem/components/RadioField/RadioField";
import { mergeClassNames } from "@bokio/utils/classes";

import { Validation } from "../Form";
import Field from "../Form/Field/Field.internals";
import { FormContext } from "../Form/Form";
import { Label } from "../Form/LabelFor/Label";

import type { UnTypedInputFieldProps } from "../Form/InputField";
import type { SelectFieldOption } from "../Form/SelectField";
import type { ReactNode } from "react";

import * as styles from "./radioFieldGroup.scss";

export type RadioFieldOption = SelectFieldOption & { inputTestId?: string };

export interface RadioFieldGroupProps extends UnTypedInputFieldProps {
	name: string;
	/** Label displayed above the radio group */
	label?: ReactNode;
	options: RadioFieldOption[];
	/** Layout direction of the radio options */
	layout?: "vertical" | "horizontal";
	radioButtonClass?: string;
	/**
	 * The class name that targets the <span> inside of each item's <label> DOM element.
	 *
	 * If you are looking for the per-item <label> class name, use the className in `options` property.
	 */
	labelClassName?: string;
	optionClassName?: string;
	/**
	 * The class name that applies to <Label className={...}> component.
	 */
	topLabelClassName?: string;
	noMarginOnLastRadio?: boolean;
}

export const RadioFieldGroup = ({
	label,
	options,
	wrapperClassName,
	labelClassName,
	errors,
	layout,
	testId,
	onChange,
	radioButtonClass,
	disabled,
	value,
	name,
	optionClassName,
	topLabelClassName,
	noMarginOnLastRadio,
}: RadioFieldGroupProps) => {
	const { disableAllFields } = use(FormContext);
	const isVerticalLayout = layout === "vertical";

	return (
		<Field className={mergeClassNames(styles.field, wrapperClassName)} testId={testId}>
			<div className={styles.label}>
				{label && <Label label={label} className={mergeClassNames(styles.innerLabel, topLabelClassName)} />}
				<div className={mergeClassNames(styles.options, isVerticalLayout && styles.verticalOptions, optionClassName)}>
					{options.map((item, i) => {
						const itemDisabled = disableAllFields || disabled || item.disabled;
						return (
							<Fragment key={item.value}>
								<RadioField
									name={name}
									value={item.value}
									checked={value === item.value}
									disabled={itemDisabled}
									label={item.label}
									testId={item.testId}
									inputTestId={item.inputTestId}
									onChange={onChange}
									className={mergeClassNames(
										isVerticalLayout ? styles.verticalRadio : styles.radio,
										noMarginOnLastRadio && i + 1 == options.length && styles.noMarginOnLastRadio,
										radioButtonClass,
										item.className,
									)}
									labelClassName={labelClassName}
								/>
							</Fragment>
						);
					})}
				</div>
				<Validation testId={`Validation_Inline_${testId}`} errors={errors && errors.errors} />
			</div>
		</Field>
	);
};
